import React, { useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../constants/api";
import KundeCard from "../Kunden/KundeCard.jsx";
import { smartBack } from "../../constants/nav";
import ListContainer from "../ui/ListContainer";
import FilterBarOrDrawer from "../ui/FilterBarOrDrawer.jsx";
import SortButton from "../ui/SortButton.jsx";
import { useAuth } from "../ui/AuthContext.jsx";
import useEnterpriseListController from "../../src/hooks/useEnterpriseListController";
import { useResponsive } from "../../hooks/useResponsive.js";
import { DEFAULT_PAGE_SIZE } from "../../constants/pagination";
import useListPaginationUi from "../../src/hooks/useListPaginationUi";
import { canEnterHoursUnlimited } from "../../constants/roles.js";
import AppButton from "../ui/AppButton.jsx";
import { useAcademyContextEvent } from "../../hooks/useAcademyContextEvent.js";
import { Input } from "../forms/shared/FormAtoms.jsx";
import {
  detailPageHeaderNavClassName,
  detailPageHeaderNavTouchClassName,
  detailPageHeaderNavActionsClassName,
  detailPageHeaderNavPrimaryMobileClassName,
  listSortTriggerClassName,
} from "../../design/tokens.js";
import { cn } from "../../utils/themeClasses.js";

const KUNDEN_SORT_OPTIONS = [
  { key: "firma", label: "Firma" },
  { key: "name", label: "Name" },
  { key: "ort", label: "Ort / PLZ" },
  { key: "plz", label: "PLZ" },
  { key: "neueste", label: "Neueste zuerst", fixedDir: "desc" },
  { key: "aelteste", label: "Älteste zuerst", fixedDir: "asc" },
];

const kundenSortDefaultDir = (key) => (key === "neueste" ? "desc" : "asc");
const kundenSortAllowDirToggle = (key) => !["neueste", "aelteste"].includes(key);

export default function KundenListe() {
  const { isDesktopXl } = useResponsive();
  const location = useLocation();
  const navigate = useNavigate();
  useAcademyContextEvent("visit_kunden");

  const {
    items: apiItems,
    loading,
    error: apiError,
    search,
    setSearch,
    sort,
    setSort,
    debouncedSearch,
    pagination,
    setPage,
    total,
    pages,
  } = useEnterpriseListController({
    fetchFn: async (queryParams, signal) => {
      const res = await api.get(`/kunden`, {
        params: {
          q: queryParams.q,
          sort: queryParams.sort,
          dir: queryParams.dir,
          page: queryParams.page,
          pageSize: queryParams.pageSize,
        },
        signal,
      });
      const items = Array.isArray(res.data?.items) ? res.data.items : Array.isArray(res.data) ? res.data : [];
      const tot = res.data?.total ?? res.data?.items?.length ?? 0;
      return {
        items,
        total: tot,
        page: res.data?.page ?? queryParams.page ?? 1,
        pageSize: res.data?.pageSize ?? queryParams.pageSize ?? DEFAULT_PAGE_SIZE,
      };
    },
    searchConfig: {
      debounceMs: 300,
      urlSync: true,
      storageKey: "kunden_q",
    },
    sortConfig: {
      defaultKey: "name",
      defaultDir: "asc",
      allowedKeys: ["name", "firma", "vorname", "nachname", "ort", "plz", "erstellt_am", "id"],
      urlSync: true,
      storageKey: "kunden_sort",
      defaultDirFor: (key) => {
        if (key === "id" || key === "erstellt_am") return "desc";
        return "asc";
      },
    },
    paginationConfig: {
      defaultPageSize: DEFAULT_PAGE_SIZE,
      urlSync: true,
    },
    urlSyncConfig: {
      enabled: true,
      pathname: location.pathname,
    },
    cacheConfig: {
      domain: "kunden",
    },
    rememberListReturn: true,
  });

  const sortUi = useMemo(() => {
    if (sort.key === "erstellt_am" && sort.dir === "desc") return { key: "neueste", dir: "desc" };
    if (sort.key === "erstellt_am" && sort.dir === "asc") return { key: "aelteste", dir: "asc" };
    return sort;
  }, [sort]);

  const setSortCombined = useCallback(
    ({ key, dir }) => {
      if (key === "neueste") setSort({ key: "erstellt_am", dir: "desc" });
      else if (key === "aelteste") setSort({ key: "erstellt_am", dir: "asc" });
      else setSort({ key, dir: dir ?? kundenSortDefaultDir(key) });
    },
    [setSort],
  );

  const { user: authUser } = useAuth();
  const darfErstellen = canEnterHoursUnlimited(authUser);

  const grad = "min-h-screen w-full bg-gradient-to-br from-[#05070c] via-[#0a1020] to-[#0a1a32] text-white";
  const frame = "app-frame";
  const textMuted = "text-blue-200/80";

  const renderKundeCard = useCallback((k) => <KundeCard kunde={k} />, []);

  const { summary: listSummary, footer: paginationFooter } = useListPaginationUi({
    loading,
    total,
    pagination,
    pages,
    setPage,
    debouncedSearch,
  });

  return (
    <div className={grad}>
      <div className="mx-auto max-w-6xl px-4 py-6">
        <div className={cn("rounded-2xl px-4 py-3 mb-6 border", frame)}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h1 className="text-2xl font-bold tracking-tight">👥 Kundenübersicht</h1>
              <p className={`mt-1 text-sm ${textMuted}`} aria-live="polite">
                {listSummary}
              </p>
            </div>
            <div className={detailPageHeaderNavActionsClassName}>
              {darfErstellen && (
                <AppButton
                  type="button"
                  variant="primary"
                  size="lg"
                  className={`${detailPageHeaderNavTouchClassName} ${detailPageHeaderNavPrimaryMobileClassName}`}
                  onClick={() => navigate("/kunden/neu")}
                >
                  <span className="md:hidden">Neu</span>
                  <span className="hidden md:inline">Neuer Kunde</span>
                </AppButton>
              )}
              <AppButton
                type="button"
                variant="secondary"
                size="lg"
                className={detailPageHeaderNavClassName}
                title="Zu Meine Aufgaben"
                onClick={() => navigate("/meine-aufgaben")}
              >
                Meine Aufgaben
              </AppButton>
              <AppButton
                type="button"
                variant="secondary"
                size="lg"
                className={detailPageHeaderNavClassName}
                onClick={() => smartBack(navigate, ["/", "/dashboard"])}
              >
                Zurück
              </AppButton>
              <AppButton
                type="button"
                variant="secondary"
                size="lg"
                className={detailPageHeaderNavClassName}
                title="Zum Dashboard"
                onClick={() => navigate("/")}
              >
                Dashboard
              </AppButton>
            </div>
          </div>
        </div>

        <div className={cn("rounded-xl px-2.5 py-2 border mb-6", frame)}>
          <div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:gap-2">
            <div className="relative w-full min-w-0 xl:flex-1 xl:min-w-[20rem]">
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Suche…"
                title="Firma, Name, Adresse, PLZ/Ort, Telefon, E-Mail, ID …"
                className={`px-3 py-2 min-h-[40px] w-full ${search ? "pr-12" : "pr-3"} text-[16px] xl:text-sm`}
                aria-label="Kunden suchen"
              />
              {search ? (
                <AppButton
                  type="button"
                  variant="ghost"
                  onClick={() => setSearch("")}
                  className="absolute right-1 top-1/2 -translate-y-1/2 !min-h-[36px] !min-w-[36px] !p-0 !border-0 !bg-transparent !shadow-none text-blue-200/70 hover:!bg-transparent hover:text-white"
                  aria-label="Suche löschen"
                  title="Suche löschen"
                >
                  ✕
                </AppButton>
              ) : null}
            </div>

            {isDesktopXl ? (
              <div className="flex flex-wrap items-center gap-2 min-w-0 xl:ml-auto xl:flex-nowrap xl:justify-end">
                <div className="flex shrink-0 items-center gap-2">
                  <SortButton
                    value={sortUi}
                    onChange={setSortCombined}
                    options={KUNDEN_SORT_OPTIONS}
                    defaultDirFor={kundenSortDefaultDir}
                    allowDirToggle={kundenSortAllowDirToggle}
                    className={cn(listSortTriggerClassName, "text-sm shrink-0")}
                  />
                </div>
              </div>
            ) : (
              <div className="w-full min-w-0">
                <FilterBarOrDrawer title="Filter & Sortierung" drawerUntil="xl" className="w-full justify-center">
                  <SortButton
                    value={sortUi}
                    onChange={setSortCombined}
                    options={KUNDEN_SORT_OPTIONS}
                    defaultDirFor={kundenSortDefaultDir}
                    allowDirToggle={kundenSortAllowDirToggle}
                    className={cn(listSortTriggerClassName, "text-sm w-full min-h-[40px] justify-center")}
                  />
                </FilterBarOrDrawer>
              </div>
            )}
          </div>
        </div>

        <ListContainer
          items={apiItems}
          loading={loading}
          error={apiError}
          emptyMessage={debouncedSearch ? "Keine Kunden für diese Suche." : "Keine Kunden gefunden."}
          gridCols="grid-cols-1 md:grid-cols-2"
          gap="gap-3"
          renderItem={renderKundeCard}
          footer={paginationFooter}
        />
      </div>
    </div>
  );
}
